import { PLCAddress, PLCConnectionPin } from "../TemplateBuilderInterfaces";
import { LiteTemplate } from "./ILiteTemplate";
import { LiteLookUpVariableData, ReferenceTypeData } from "./LiteProjectDto";

export class LiteProject {
    liteProjectId: string;
    liteProjectName: string;
    cadSoftware: string;
    suggestRating: boolean;
    controllersIOConfigurations: LiteProjectControllerIO[] = [];
    numberOfGenerations: number;
    fileName: string = '';
    fileUrl: string = '';
    userId: string;
    createdFromSchematic: boolean;
}

export class LiteProjectControllerIO {
    liteProjectControllerIOId: string;
    controllerIOName: string;
    manufacturerId: string;
    manufacturerName?: string; // Optional because of BsonIgnore
    seriesId: string;
    seriesName?: string; // Optional because of BsonIgnore
    panelComponentId: string;
    startingSlotNumber: number = 0;
    startingSlotNumberToLeft: number = 100;
    ioConfigurations?: LiteIOConfiguration[];
}

export class LiteIOConfiguration {
    liteIOConfigurationId: string;
    panelComponentId: string;
    controllerIOType: string;
    ioConfigurationType: number;
    modules?: LiteIOModule[];
    macroVarient?: LiteMacroVarient;
    isWhiteBaseMacro: boolean;
    primaryIOTypeId: string;
    isEmptyModule: boolean;
    slotRequired: boolean = true;
    accessory?: LiteControllerIOAccessory;
    orderNumberString: string;
    orderNo: number;
    name: string;
    tag?: string;
    partNumber?: string;
    height?: string;
    depth?: string;
    width?: string;
    manufacturerName?: string;
    seriesName?: string;
}

export class LiteControllerIOAccessory {
    panelComponentId: string;
    partNumber?: string;
    height?: string;
    depth?: string;
    width?: string;
    manufacturerName?: string;
    seriesName?: string;
}

export class LiteMacroVarient {
    componentId: string;
    accessoryIncluded: boolean;
    varientId: number;
    mainOverviewMacro?: LiteMacro;
    referenceOverviewMacro?: LiteMacro;
    offset: number;
    terminalBaseLogic: number;
}

export class LiteIOModule {
    ioTypeId: string;
    defaultIOTypeId: string;
    containsMultipleIOType: boolean;
    supportedIOTypeIds?: string[];
    name?: string; // Optional because of BsonIgnore
    ioTypeChannelType?: number; // Nullable in C#, so optional in TS
    inputOrOutput?: number; // Nullable in C#, so optional in TS
    isIncrementable: boolean;
    baudRateAndParity: boolean;
    baudRate: string;
    parity: string;
    addresses?: LiteIOModuleAddress[];
    plcAddress?: PLCAddress;
    plcConnectionPin?: PLCConnectionPin;
}

export class LiteIOModuleAddress {
    assignedIOTypeId: string;
    ioModuleAddressId: string;
    channel: number;
    associatedPLCConnectionPinDesignation?: PLCConnectionPin;
    assignedTo?: LiteProjectControllerIOSocket;
    addressStructure?: LiteIOConfigAddressStructure[];
}

export class LiteProjectControllerIOSocket {
    templateId: number;
    pageId: number;
    componentPlugKey: string;
    caseId: number;
    optionId: number;
    templateSocketId: number;
    componentId: number;
    useRedundancy: boolean;
    workspaceComponentType?: number;
    ioTypeId?: string;
    addressNotAssigned?: boolean;
}

export class LiteIOConfigAddressStructure {
    addressStructureId: number;
    type: number;
    stringValue: string;
    numberSystem?: number;
    modulus?: number;
    option?: number;
    incrementValue?: number;
    startingAddress?: number;
}

export class LiteMacro {
    id: string;
    name: string;
    description: string;
    componentType: string;
    variables: LiteVariable[];
    file: string;
    fileUrl: string;
    image: string;
    imageUrl: string;

    // Conditional and only appears in ISPLC API
    productGroup: number;
    type: number;
    isPLC: boolean;
}

export class LiteVariable {
    liteVariableId: string;
    variableName: string;
    description: string;
    source: VariableSource;
}

export class LiteVariableDetails {
    liteVarableId: string
    referenceTypeItems: VariableSource[];
    pageId?: number;
    socketId?: number;
    macroInSocketId: string;
    referenceType: number;
    orderNumber: number;
    relateToPLC: boolean;
}

export class VariableSource {
    variableSourceId: number;
    stringValue: string;
    componentTypeConfiguredColumnId: number;
    incrementedVariableSourceId: number;
    customItems: VariableSource[];
    plcDescriptionItems: VariableSource[];
    referenceTypeItems: VariableSource[];

    // This value: any;
    value: string;

    // Ignored values
    isChangedFromGroup: boolean;
}

export class LiteTemplateSelectorRootCase {
    templateId: string;
    cases: LiteTemplateSelectorCase[];
    pages: LiteTemplateSelectorPage[];
}

export class LiteTemplateSelectorCase {
    caseId: string;
    caseName: string;
    options: LiteTemplateSelectorOption[];
    selectedOptionId?: number; // '?' denotes that the property is optional
    isSeries: boolean;
    isPLC: boolean;
    parentCaseId: string;
    isParent: boolean;
    depedency: LiteCaseDependency[] = [];
    order: number = 0;
}

export class LiteTemplateSelectorOption {
    optionId: number;
    seriesId: string;
    optionName: string;
}

export class LiteCaseDependency {
    caseId: string;
    optionId: number;
    dependendCaseIds: string[];
}

class LiteTemplateSelectorPage {
    pageId: number;
    selectedPageCaseId?: number; // '?' denotes that the property is optional
    outputFileName: string;
    outputFileUrl: string; // Assuming the URL will be a string in TypeScript
}

export class LiteTemplateSelectorTemplate {
    templateId: string;
    template: LiteTemplate;
    templatePages: LiteTemplateSelectorPage[]
}

export class LiteSchematic {
    id: string;
    liteProjectId: string;
    projectControllerIOIds?: string[];
    addressAssignmentStatus: number = 1;
    template: LiteTemplate;
    componentType: LiteComponentType;
    referenceTypeData: ReferenceTypeData;
    liteLookUpVariableData: LiteLookUpVariableData;
    caseConfigurations: LiteTemplateSelectorRootCase;
}

export class LiteComponentType {
    componentTypeId: string;
    componentTypeDescription: string;
    componentTypeName?: string;
    tagDefinition: string;
    taglistColumns: TaglistColumn[] = [];
    imageUrl?: string;
    image?: string;
}

export class TaglistColumn {
    taglistColumnLabel: string;
    fieldId: number;
    options?: string[];
    type?: number;
    isRequired: boolean;
    defaultValue: string;
    value: string; // This value will be assigned from workspace project
    enableInIOList: boolean = false;
}

export class LiteAddCardDto {
    addedCardType: number;
    componentId: string;
    ioTypeId: string;
    accessoryComponentId: string;
    ioConfigurationId: string;
}

export class CaseOptionMapping {
    mappingId: number;
    sourceCaseId: string;
    sourceCase: LiteTemplateSelectorCase;
    mappingOptions: MappingOptions[];

    isDataChanged?: boolean; // For frontend
    isNew?: boolean// For frontend
}

export class MappingOptions {
    sourceOptionIds: number[];
    targetCases: TargetCaseDetail[];
}

export class TargetCaseDetail {
    caseId: string;
    targetCase: LiteTemplateSelectorCase;
    options: number[];
}